import React, { type PropsWithChildren, useCallback, useEffect } from 'react';
import { type StyleProp, type ViewStyle } from 'react-native';
import Animated, {
  Easing,
  useAnimatedStyle,
  useSharedValue,
  withDelay,
  withTiming,
} from 'react-native-reanimated';

// ─────────────────────────────────────────────────────────────────────────────

export const BagFeaturedUpsellReveal = (props: Props) => {
  const { order, style, children } = props;

  // ─── Refs ──────────────────────────────────────────────────────────────────

  const progress = useSharedValue(0);

  // ─── Handlers ──────────────────────────────────────────────────────────────

  const reveal = useCallback(() => {
    const delay = REVEAL_DELAY + order * REVEAL_STAGGER;

    progress.value = withDelay(delay, withTiming(1, REVEAL_TIMING_CONFIG));
  }, [order, progress]);

  // ─── Effects ───────────────────────────────────────────────────────────────

  useEffect(reveal, [reveal]);

  // ─── Styles ────────────────────────────────────────────────────────────────

  const revealAnimatedStyle = useAnimatedStyle(() => ({
    opacity: progress.value,
    transform: [{ translateY: (1 - progress.value) * REVEAL_OFFSET }],
  }));
  const revealStyles = [style, revealAnimatedStyle];

  // ───────────────────────────────────────────────────────────────────────────

  return <Animated.View style={revealStyles}>{children}</Animated.View>;
};

// ─── Constants ───────────────────────────────────────────────────────────────

const REVEAL_DELAY = 450;
const REVEAL_STAGGER = 198;
const REVEAL_OFFSET = 12;
const REVEAL_TIMING_CONFIG = { duration: 630, easing: Easing.bezier(0.33, 1, 0.68, 1) };

// ─── Types ───────────────────────────────────────────────────────────────────

type Props = PropsWithChildren<{
  order: number;
  style?: StyleProp<ViewStyle>;
}>;
